import { useEffect, useState } from 'react';
import { adminApi, type ConnectionRow } from './api';
import { Card, Tag, confirmAction, formatDateTime, useSaver } from './ui';

/**
 * „KI verbinden“: Der Verwalter trägt die Adresse des MCP-Anschlusses in Claude oder ChatGPT ein, meldet sich auf
 * der Erlauben-Seite an – fertig. Hier stehen die Anleitung, die bestehenden Verbindungen (mit Trennen) und für
 * Profis der Hinweis auf Hand-Schlüssel (Einstellungen).
 */
export function ConnectPage() {
  const origin = window.location.origin;
  const mcpUrl = `${origin}/mcp`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(mcpUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <>
      <div className="adm-head">
        <div>
          <h1>KI verbinden</h1>
          <p className="muted">Lass Claude oder ChatGPT deine Akademie bedienen: Kurse anlegen, Texte schreiben, Videos einspielen, Kunden freischalten. Du bleibst der Chef – jede Verbindung hat nur die Rechte, die du ihr gibst.</p>
        </div>
      </div>

      <Card title="Adresse deiner Akademie für KI-Assistenten">
        <div className="row">
          <input type="text" readOnly value={mcpUrl} onFocus={(e) => e.currentTarget.select()} />
          <button type="button" className="button button--secondary" onClick={copy}>{copied ? 'Kopiert ✓' : 'Kopieren'}</button>
        </div>
        <p className="muted small" style={{ marginTop: 10 }}>Diese Adresse ist kein Geheimnis – ohne deinen Login kann niemand etwas damit anfangen.</p>
      </Card>

      <Card title="So verbindest du Claude">
        <ol className="steps-list">
          <li>In Claude (claude.ai oder die Claude-App) auf <strong>Einstellungen → Connectors</strong> gehen und <strong>„Eigenen Connector hinzufügen“</strong> wählen.</li>
          <li>Name: <strong>Evercourse</strong>, Adresse: die oben kopierte.</li>
          <li>Auf <strong>Verbinden</strong> klicken. Es öffnet sich die Erlauben-Seite deiner Akademie: anmelden, Rechte wählen, <strong>Erlauben</strong>.</li>
          <li>Zurück in Claude einfach loslegen: <em>„Leg einen Kurs ‚Hundephysiotherapie für Zuhause‘ mit 8 Lektionen an.“</em></li>
        </ol>
        <p className="muted small">ChatGPT: Einstellungen → Connectors → Erstellen, gleiche Adresse. Claude Code (Terminal): <code>claude mcp add --transport http evercourse {mcpUrl}</code>.</p>
      </Card>

      <ConnectionsCard />

      <Card title="Was die KI kann – und was nicht">
        <ul className="plain-list">
          <li><strong>Kann:</strong> Kurse, Module, Lektionen anlegen und ändern · Beschreibungen und Kurztitel schreiben · Sprungmarken setzen · Kursbilder, Workbooks und Videos aus öffentlichen Links oder Google Drive / Dropbox einspielen · Produkte mit Preis · Kunden freischalten · Mailvorlagen · Erscheinungsbild und Rechtstexte · Protokoll lesen.</li>
          <li><strong>Kann nicht:</strong> dein Passwort ändern, Schlüssel verwalten, Verbindungen kappen – und löschen nur, wenn du beim Verbinden „Löschen“ erlaubt hast.</li>
          <li><strong>Immer:</strong> Neues entsteht als Entwurf. Veröffentlicht wird, wenn du es sagst. Jede Änderung steht im Protokoll (Einstellungen).</li>
        </ul>
        <p className="muted small">Für Profis: Statt der Verbindung geht auch ein Hand-Schlüssel (Einstellungen → Schnittstelle), zum Beispiel für eigene Programme oder den lokalen Evercourse-MCP.</p>
      </Card>
    </>
  );
}

function ConnectionsCard() {
  const [rows, setRows] = useState<ConnectionRow[] | null>(null);
  const [scopes, setScopes] = useState<{ scope: string; title: string }[]>([]);
  const saver = useSaver();
  const load = () => adminApi.listConnections().then((r) => { setRows(r.connections); setScopes(r.scopes); }).catch(() => setRows([]));
  useEffect(() => void load(), []);
  const title = (s: string) => scopes.find((x) => x.scope === s)?.title ?? s;
  return (
    <Card title="Bestehende Verbindungen">
      {rows === null ? <p className="muted">Wird geladen …</p> : rows.length === 0 ? <p className="muted">Noch keine Verbindung. Sobald ein Assistent verbunden ist, steht er hier.</p> : (
        <table className="table">
          <thead><tr><th>Assistent</th><th>Rechte</th><th>Verbunden</th><th>Zuletzt benutzt</th><th></th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.clientId}>
                <td><strong>{c.clientName ?? 'Unbenannt'}</strong>{c.adminEmail && <div className="muted small">{c.adminEmail}</div>}</td>
                <td>{c.scopes.map((s) => <Tag key={s} tone={s === 'danger' ? 'warn' : 'muted'}>{title(s)}</Tag>)}</td>
                <td className="muted">{formatDateTime(c.connectedAt)}</td>
                <td className="muted">{c.lastUsedAt ? formatDateTime(c.lastUsedAt) : '–'}</td>
                <td className="row-actions">
                  {c.active ? (
                    <button type="button" className="link-button small link-button--danger" disabled={saver.busy} onClick={() => { if (confirmAction(`Verbindung „${c.clientName ?? 'Unbenannt'}“ trennen? Der Assistent muss sich dann neu verbinden.`)) void saver.run(async () => { await adminApi.disconnect(c.clientId); await load(); }); }}>Trennen</button>
                  ) : <Tag tone="muted">getrennt</Tag>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {saver.error && <div className="error">{saver.error}</div>}
    </Card>
  );
}
